import { Badge, Checkbox, Group, Stack, Text } from '@mantine/core'
import type { useListState } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { contrastTextColor } from '@/components/Navigation/utils/mapGLHelpers'

export type VehicleItem = { id: string; color: string; visible: boolean }
type ListHandlers = ReturnType<typeof useListState<VehicleItem>>[1]

export function DriveMapLegend({
  items,
  handlers,
  driveCounts,
}: {
  items: VehicleItem[]
  handlers: ListHandlers
  driveCounts: Record<string, number>
}) {
  const { t } = useTranslation('scheduling')
  if (items.length === 0) return null

  const visibleCount = items.reduce((sum, item) => (item.visible ? sum + 1 : sum), 0)
  const allVisible = visibleCount === items.length
  const allHidden = visibleCount === 0

  const toggleAll = () => {
    const next = !allVisible
    handlers.setState(items.map(item => ({ ...item, visible: next })))
  }

  return (
    <Stack gap={4}>
      <Text fw={600} size="sm">
        {t('legendVehicles')}
      </Text>
      <Checkbox
        checked={allVisible}
        indeterminate={!allVisible && !allHidden}
        onChange={toggleAll}
        label={allVisible ? t('deselectAll') : t('selectAll')}
      />
      <Stack gap="sm" pl="lg">
        {items.map((item, index) => {
          const count = driveCounts[item.id] ?? 0
          return (
            <Checkbox
              key={item.id}
              checked={item.visible}
              onChange={event => handlers.setItemProp(index, 'visible', event.currentTarget.checked)}
              label={
                <Group gap={8} wrap="nowrap" justify="space-between" w="100%">
                  <Badge
                    variant="filled"
                    radius="sm"
                    style={{
                      backgroundColor: item.color,
                      color: contrastTextColor(item.color),
                      minWidth: 0,
                    }}
                  >
                    {item.id}
                  </Badge>
                  <Badge size="xs" variant="light" color="gray">
                    {count}
                  </Badge>
                </Group>
              }
              styles={{
                body: { alignItems: 'center', width: '100%' },
                labelWrapper: { width: '100%' },
                label: { width: '100%', paddingInlineStart: 8 },
              }}
            />
          )
        })}
      </Stack>
    </Stack>
  )
}
